import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  BINDABLE_ACTIONS,
  bindingFromKey,
  rebind,
  UI_SCALE_RANGE,
  useSettingsStore,
  type BindableAction,
  type ChatFontSize,
  type ReduceMotion,
} from '../state/settings.js';
import { LanguageSwitch } from './Hud.js';

/**
 * Accessibility and comfort settings (spec §14): UI scale, chat font size, darkness, reduce
 * motion, volume, keys and language. Changes apply at once and are kept in this browser.
 */
export function SettingsPanel({
  onClose,
  onReplayTutorial,
}: {
  onClose: () => void;
  onReplayTutorial?: () => void;
}) {
  const { t, i18n } = useTranslation();
  const settings = useSettingsStore();
  const closeRef = useRef<HTMLButtonElement>(null);
  const [listening, setListening] = useState<BindableAction>();
  const [tipOpen, setTipOpen] = useState(false);
  const { up, left, down, right } = settings.keys;
  const moveKeys = [up, left, down, right].map((k) => k.label).join('');

  useEffect(() => {
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    closeRef.current?.focus();
    return () => opener?.focus();
  }, []);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (listening) {
        // Capture the next key for the binding; Escape cancels.
        event.preventDefault();
        event.stopImmediatePropagation();
        if (event.key !== 'Escape') {
          const binding = bindingFromKey(event.key, event.keyCode);
          if (!binding) return;
          const { keys, update } = useSettingsStore.getState();
          update({ keys: rebind(keys, listening, binding) });
        }
        setListening(undefined);
        return;
      }
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey, { capture: true });
    return () => window.removeEventListener('keydown', onKey, { capture: true });
  }, [listening, onClose]);

  const percent = (value: number) => `${Math.round(value)} %`;

  return (
    <div className="overlay settings-overlay" onClick={onClose}>
      <div
        className="panel settings-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="settings-title"
        onClick={(event) => event.stopPropagation()}
      >
        <h1 id="settings-title">{t('settings.title')}</h1>

        <section aria-labelledby="settings-display">
          <h2 id="settings-display">{t('settings.display')}</h2>
          <label className="setting">
            <span>{t('settings.uiScale')}</span>
            <input
              type="range"
              min={UI_SCALE_RANGE.min}
              max={UI_SCALE_RANGE.max}
              step={UI_SCALE_RANGE.step}
              value={settings.uiScale}
              onChange={(event) => settings.update({ uiScale: Number(event.target.value) })}
            />
            <output>{percent(settings.uiScale * 100)}</output>
          </label>
          <label className="setting">
            <span>{t('settings.chatFontSize')}</span>
            <select
              value={settings.chatFontSize}
              onChange={(event) =>
                settings.update({ chatFontSize: event.target.value as ChatFontSize })
              }
            >
              {(['small', 'medium', 'large'] as const).map((size) => (
                <option key={size} value={size}>
                  {t(`settings.fontSizes.${size}`)}
                </option>
              ))}
            </select>
          </label>
          <label className="setting">
            <span>{t('settings.darkness')}</span>
            <input
              type="range"
              min={0}
              max={100}
              step={5}
              value={settings.darkness}
              onChange={(event) => settings.update({ darkness: Number(event.target.value) })}
            />
            <output>{percent(settings.darkness)}</output>
          </label>
          <label className="setting">
            <span>{t('settings.reduceMotion')}</span>
            <select
              value={settings.reduceMotion}
              onChange={(event) =>
                settings.update({ reduceMotion: event.target.value as ReduceMotion })
              }
            >
              {(['system', 'on', 'off'] as const).map((option) => (
                <option key={option} value={option}>
                  {t(`settings.motion.${option}`)}
                </option>
              ))}
            </select>
          </label>
          <p className="muted small">{t('settings.reduceMotionHint')}</p>
        </section>

        <section aria-labelledby="settings-keys">
          <h2 id="settings-keys">
            {t('settings.keys')}
            <span className="help-tip">
              <button
                type="button"
                className="help-tip-button"
                aria-label={t('settings.controlsHelp')}
                aria-describedby="settings-controls-tip"
                aria-expanded={tipOpen}
                onClick={() => setTipOpen((open) => !open)}
                onBlur={() => setTipOpen(false)}
              >
                ?
              </button>
              <span
                id="settings-controls-tip"
                role="tooltip"
                className={tipOpen ? 'help-tip-text open' : 'help-tip-text'}
              >
                {t('hud.controls', {
                  move: moveKeys,
                  sprint: settings.keys.sprint.label,
                  interact: settings.keys.interact.label,
                })}
              </span>
            </span>
          </h2>
          <p className="muted small">{t('settings.keysHint')}</p>
          <ul className="key-bindings">
            {BINDABLE_ACTIONS.map((action) => (
              <li key={action}>
                <span>{t(`settings.actions.${action}`)}</span>
                <button
                  type="button"
                  className="secondary"
                  aria-pressed={listening === action}
                  onClick={() => setListening(listening === action ? undefined : action)}
                >
                  {listening === action ? (
                    t('settings.pressKey')
                  ) : (
                    <kbd>{settings.keys[action].label}</kbd>
                  )}
                </button>
              </li>
            ))}
          </ul>
        </section>

        <section aria-labelledby="settings-language">
          <h2 id="settings-language">{t('hud.language')}</h2>
          <LanguageSwitch current={i18n.language} label={t('hud.language')} />
        </section>

        <div className="settings-actions">
          {onReplayTutorial && (
            <button type="button" className="secondary" onClick={onReplayTutorial}>
              {t('settings.replayTutorial')}
            </button>
          )}
          <button type="button" className="secondary" onClick={() => settings.reset()}>
            {t('settings.reset')}
          </button>
          <button ref={closeRef} type="button" onClick={onClose}>
            {t('common.close')}
          </button>
        </div>
      </div>
    </div>
  );
}
